/* -------------------------------------------------------------------------- */
/*                                   TITLES                                   */
/* -------------------------------------------------------------------------- */
const titles = {
	pbs: "Proxmox Backup Server",
	pbs_sync: "PBS sync",
};

/* -------------------------------------------------------------------------- */
/*                                 SELECT TYPE                                */
/* -------------------------------------------------------------------------- */
const selectType = {
	card_bttn: "Configure",

	// Products
	pbs_free: {
		description: "150 GB free storage",
		disabled_info: "Max. 1 per customer",
		title: "Tuxis PBS - Free",
	},
	pbs_paid: {
		description: "Full version for business application.",
		disabled_info: "Paid services not allowed",
		title: "Tuxis PBS",
	},
	pbs_sync: {
		description: "Synchronize a datastore.",
		disabled_info: "Paid services not allowed",
		title: "PBS sync",
	},
	price_suffix: "p/m per",
	title: "Which Proxmox Backup service would you like to create?",
};

/* -------------------------------------------------------------------------- */
/*                                FORM SECTION                                */
/* -------------------------------------------------------------------------- */
const pbsConfigDescription = `
Define a name and specify how you want to be billed and when you want to receive a notification.

**Fixed limit** 
Your datastore does not grow automatically and can therefore run out of space if you do not increase in time. You can set up notifications to prevent this. You will be billed for actual usage. The size of the datastore is irrelevant for invoicing.

**Grow as you go** 
Your datastore is not limited but grows automatically when it is almost full. You will only be billed for actual usage. The size of the datastore is irrelevant for billing.
`;

const pbsResellerDescription =
	"When you link the datastore to a customer, this customer gets its own login credentials to log into the datastore.\nAs a reseller, you also have access to this datastore with your own login credentials.";

const pbsSyncVariantDescription = `

**Tuxis PBS**
The datastore you want to synchronize is a Tuxis PBS datastore.

**Remote PBS**
The datastore you want to synchronize is located on a PBS at a different location.`;

const form = {
	// PBS ALERT
	pbs_alert: {
		alert_pruning: {
			label: "(Optional) Also send an e-mail in case of error messages about pruning",
			tip: "Pruning is a configurable task of a PBS to clean up old snapshots. If an error message occurs during a pruning job, this error message will only be emailed to the email address for PBS error messages if this checkbox is ticked.",
		},
		description:
			"Enter an email address where you want to receive the PBS error messages and optionally choose a relation you want to link this account to. Linking a relation does not otherwise affect its operation. You can also link other products to a relation and thus see what a relation uses.",
		pbs_error_email_address: {
			helper_text: {
				business_debtor: {
					href: "/company?scroll_to_communication_preferences=true",
					text: "PBS error messages will be sent to the above email address. You can change this email address <a>on the company details page</a>.",
				},
				private_debtor: {
					href: "/account",
					text: "PBS error messages will be sent to to the above email address. You can change this email address <a>on the account details page</a>.",
				},
			},
			label: "Email address for PBS error messages",
			placeholder: "For example foutmeldingen@example.nl",
		},
		relation: {
			label: "Link to relation",
			option: "Link PBS to a relation",
		},
		title: "Other information",
	},

	// PBS DATASTORE
	pbs_datastore: {
		datastore_alert: {
			alert_option_auto_expand:
				"Send me an alert email when the datastore has <strong>more than {{value}}</strong> in use.",
			alert_option_limited_size:
				"Send me an alert email when the datastore has <strong>less than {{value}}</strong> free space.",
			label: "Set notifications",
		},
		datastore_config: {
			alert_email_address: {
				helper_text: "Send an email to this address at the notification notifications set above.",
				label: "Email address for notifications",
				placeholder: "For example something@domainname.nl",
			},
			auto_expand: {
				label: "Grow as you go",
				tip: "This option can only be used with paid pbs. \n Your datastore is not limited but grows automatically when it is almost at capacity. You will only be billed actual usage rounded up to whole TBs. The size of the datastore is irrelevant for billing.",
			},
			free_version: {
				title: "Datastore version",
				value: "PBS Free - 150 GB",
			},
			label: "Datastore configuration",
			limited_size: {
				label: "Datastore fixed limit",
				tip: "Your datastore does not grow automatically and thus may become full if you do not increase in time. You can set up notifications to prevent this. You will be billed the actual usage rounded to whole TBs. The datastore limit is irrelevant to billing.",
			},
		},

		datastore_info: {
			description: {
				placeholder: "Optional description",
			},
			label: "PBS datastore info",
			name: {
				helper_text: "Give the datastore an appropriate name",
				placeholder: "For example ‘production’",
			},
		},
		datastore_limit: {
			alert_info_free: "With a free PBS account, the limit cannot be adjusted.",
			alert_info_paid:
				"Your billing is based on actual usage, rounded up to the next terabyte. With your current limit, the bill will never exceed <strong>{{value}}</strong>.",
			label: "Set Datastore limit",
		},
		description: pbsConfigDescription,
		title: { pbs: "Configuration", pbs_sync: "Configuration destination PBS" },
	},

	// PBS LOCATION
	pbs_location: {
		description:
			"Here you can choose in which country and city your data will be stored. This way you can make sure that your backups are at a great distance from your live data.",
		no_locations:
			"Currently there is no capacity available and it is being expanded. For status updates, follow us on the [Announcements Page](https://support.tuxis.nl/en/announcements) or on [LinkedIn](https://nl.linkedin.com/company/tuxis-internet-engineering).",
		no_locations_free: "No free PBS can be purchased in this country at this time.",
		no_locations_paid: "No paid PBS can be purchased in this country at this time.",
		select_country: "Select a country",
		select_invalid: "Select a valid PBS server location",
		select_location: "Select a server location",
		source_and_destination_location_identical:
			"It is not allowed to configure a PBS sync to the same location as its source PBS.",
		title: {
			pbs: "PBS server location",
			pbs_sync: "Location destination PBS",
		},
	},

	// PBS RESELLER (USING PBS ALERT TEXT FOR MOST COMPONENTS)
	pbs_reseller: {
		description:
			"Enter an email address where you want to receive PBS error notifications and optionally choose a customer to link this account to. Linking a customer has no further impact on functionality. You can also link other products to a customer and see what a customer is using",
		relation: {
			info: "If the customer does not yet have login credentials for the selected PBS location, the login credentials are visible in the password vault for 30 days.",
		},

		reseller_mode: {
			info: pbsResellerDescription,
			option_list: ["Own use", "For a customer"],
			title: "Deploy datastore for",
		},
		title: "Reseller settings",
	},

	// PBS SOURCE SELECTOR
	pbs_source_selector: {
		autocomplete: {
			helper_text: "Free datastores are excluded from selection",
			label: "PBS",
			placeholder: "Select existing datastore",
			title: "Select one of your existing datastores",
		},
		description: "Select the source datastore you want to sync.",
		title: "Select source datastore",
	},

	// PBS REMOTE SOURCE SELECTOR
	pbs_source_selector_remote: {
		description: "Select the source PBS you want to synchronize from.",

		// REMOTE HOST
		remote_host: {
			host: {
				label: "Remote hostname",
				placeholder: "For example 'pbs.example.com'",
			},
			port: {
				label: "Remote PBS port",
				placeholder: "For example '8007'",
			},
			source_datastore_name: {
				label: "Datastore name",
				placeholder: "For example 'datastore_remote'",
			},
			title: "Configure remote host",
		},

		// REMOTE HOST CREDENTIALS
		remote_host_credentials: {
			title: "Remote host credentials",
			token_name: {
				label: "Token name",
				placeholder: "Enter the token name",
			},
			token_secret: {
				label: "Token secret",
				placeholder: "Enter the token secret",
			},
			user_name: {
				label: "Username",
				placeholder: "For example 'username@pbs'",
			},
		},

		// FINGER PRINT
		ssl_fingerprint: {
			checkbox: {
				label: "The PBS host uses a self-signed certificate",
				tooltip:
					"The SSL fingerprint is checked when establishing a secure connection. This is a mandatory field for self-signed certificates, but optional for official certificates. [Read more.](https://pbs.proxmox.com/docs/sysadmin.html#certificate-management)",
			},
			ssl_fingerprint: {
				placeholder: "Enter SSL fingerprint",
			},
			title: "SSL fingerprint",
		},

		// BUTTONS
		test_button: "Test connection",
		test_success: "Connection tested successfully",
		test_tooltip:
			"This is an optional test. This test will verify the information you entered to ensure that you have entered it correctly. \n This test may not work if you have a whitelist set up on your host. In that case, you can simply create a pbs-sync.",
		title: "Source PBS information",
	},

	// PBS SYNC VARIANT
	pbs_sync_variant: {
		description: pbsSyncVariantDescription,
		option_list: {
			remote_pbs: "Remote PBS",
			tuxis_pbs: "Tuxis PBS",
		},
		title: "Source PBS",
	},
	// PBS TYPE
	pbs_type: {
		description:
			"The free version is available to everyone. Only Companies and Dutch citizens can purchase the paid version.",
		title: "Tuxis PBS version",
	},

	// PBS SYNC INTERVAL
	schedule: {
		description:
			"Specify how often you want to synchronize the source datastore to the destination datastore.",
		option_list: {
			day_1: "Once per day",
			day_4: "4 times per day",
			day_12: "12 times per day",
		},
		select: {
			helper_text: "Sync intervals can be updated.",
			label: "Interval",
			placeholder: "Select an interval",
			title: "Select an interval",
		},
		title: "Sync interval",
	},

	submit_button: "Check configuration",
};

/* -------------------------------------------------------------------------- */
/*                                CONFIRM PAGE                                */
/* -------------------------------------------------------------------------- */
const confirm = {
	config: {
		alert_email_address: "Notification email address:",
		alert_info:
			"We calculate the highest storage peak measured during the month and round it up to a whole terabyte.",
		alert_send_by: {
			label: "Send notifications for",
			list: [
				"Errors detected by Proxmox Backup Server",
				"If <strong>{{value}}</strong> of storage remains",
				"If <strong>{{value}}</strong> of storage is reached",
				"Errors in Pruning",
			],
		},
		datastore_config: "Datastore configuration:",
		description: "View the overview of the PBS you are ordering here",
		fingerprint: "SSL-fingerprint",
		host: "Remote PBS hostname",
		location: "PBS Server location:",
		pbs_source_selector: "Selected Tuxis PBS",
		pbs_sync_variant: "Source PBS",
		pbs_type: "PBS version:",
		port: "Remote PBS port",
		price_discount: "Your discount",
		price_exc_vat: "Excl. VAT",
		price_line_pbs: "Per terabyte €{{value}} p/m",
		price_line_pbs_sync: "€{{value}} p/m",
		price_pbs: "Price PBS",
		price_pbs_sync: "Price PBS sync",
		relation: "Linked relation:",
		schedule: "Sync interval",
		source_datastore_name: "Remote datastore name",
		title: "Configuration overview",
		token_name: "Token name",
		token_secret: "Token secret",
		user_name: "Username",
	},

	info: {
		description: {
			label: "Description (optional)",
			placeholder: "Optional description",
		},
		name: {
			label: "Datastore name",
			placeholder: "For example 'production'",
		},
		purchase_order_number: {
			helper:
				"When you enter a purchase order number, this service will always be billed separately, and the purchase order number will be listed on the invoice.",
			label: "Purchase order number (optional)",
			placeholder: "For example '1-2344-3949-3'",
		},
		text: "Make any necessary changes to the PBS details here.",
		title: "PBS details",
	},

	product_details: {
		pbs: "Proxmox Backup Service",
		pbs_edit: "Proxmox Backup Service - Update",
		pbs_free: "Proxmox Backup Service - Free (150GB)",
		pbs_sync: "PBS sync",
		pbs_sync_edit: "PBS sync - Update",
		title: "Product overview",
	},
	status_messages: {
		no_nodes_available:
			"Unfortunately, there is currently no capacity available at the location. Try another location or contact support.",
		success:
			"Your new PBS has been successfully created! The login information can be found in the password vault.",
	},
	title: "Order overview",
};

const create = {
	confirm: confirm,
	form: form,
	select_type: selectType,
	titles: titles,
};

export default create;
